import { useHistory } from "react-router-dom";
import { MergeAccountMutation } from "src/api/User";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import LinkButton from "src/components/buttons/LinkButton";
import AccountOwnerNotifications from "src/components/forms/AccountOwnerNotifications";
import Container from "src/components/layout/Container";
import { ChangePrimaryAccountModal } from "src/components/modals/ChangePrimaryAccountModal";
import UserModel from "src/models/UserModel";
import { apiClient, queryClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import { modals } from "src/utils/modals";
import { useListBackground } from "src/utils/setListBackground";
import ItemCard from "../../../components/items/item/ItemCard";

interface Props {
  user: UserModel;
  onUserProfileUpdate: () => void;
}

export default function AccountAccess(props: Props) {
  const history = useHistory();
  const store = useStore();
  useListBackground();

  let primaryAccount = props.user;
  let linkedAccounts = primaryAccount.linkedAccounts;

  if (props.user.primaryAccount) {
    primaryAccount = props.user.primaryAccount;
    linkedAccounts = [props.user];
  }

  function renderUser(user) {
    return (
      <div className="flex">
        <div className="flex-1">
          <div>
            <strong>
              {user.firstName} {user.lastName}
            </strong>
          </div>

          <div className="flex gap-1">
            <div className="w-6 text-center">
              <i className="far fa-envelope text-organization" />
            </div>
            <div className="flex-1">{user.email}</div>
          </div>

          {user.phone && (
            <div className="flex gap-1">
              <div className="w-6 text-center">
                <i className="far fa-phone text-organization" />
              </div>
              <div className="flex-1">{user.phone}</div>
            </div>
          )}

          {user.address1 && (
            <div className="flex gap-1">
              <div className="w-6 text-center">
                <i className="far fa-location-dot text-organization" />
              </div>
              <div className="flex-1">
                {user.address1}
                <br />
                {user.city}, {user.state} {user.zip}
              </div>
            </div>
          )}
        </div>

        {(store.isAdminArea || user.id === props.user.id) && (
          <div>
            <LinkButton
              to={
                store.isAdminArea
                  ? `/admin/users/${user.id}/more`
                  : `/account/settings/users/${user.id}`
              }
            >
              <strong>Manage</strong>
            </LinkButton>
          </div>
        )}
      </div>
    );
  }

  function changePrimary() {
    modals.show(ChangePrimaryAccountModal, {
      fromUser: props.user,
      handleOk: (primary: UserModel) => {
        if (primary.id !== props.user.id) {
          mergeAccount({ fromUser: props.user, toUser: primary });
        }
      },
    });
  }

  async function mergeAccount({ fromUser, toUser }) {
    const processingMessage = message.loading("Updating…");

    try {
      const mergeResult = await apiClient(MergeAccountMutation, {
        variables: {
          from: fromUser.id,
          to: toUser.id,
          changePrimary: true,
        },
      });

      queryClient.invalidateQueries({ queryKey: ["user", toUser.id] });
      queryClient.invalidateQueries({ queryKey: ["user", fromUser.id] });

      processingMessage();
      history.push(`/admin/users/${mergeResult.mergeAccount.id}/access`);
    } catch (err) {
      message.error(err);
      processingMessage();
    }
  }

  return (
    <>
      <div className="relative z-10 bg-white shadow">
        <Container full>
          <div className="flex items-center">
            <div className="flex-1">
              <BackButton
                backText="Return to account"
                className="pt-2"
                to={
                  store.isAdminArea
                    ? `/admin/users/${props.user.id}`
                    : "/account/settings"
                }
              />

              <h1 className="mb-0">Account Access</h1>
              <p>{props.user.lastName} Account</p>
            </div>
          </div>
        </Container>
      </div>

      <Container large main className="pt-0.5">
        <ItemCard loading>
          <p className="mb-2 font-bold">Primary Account</p>
          <p>
            The primary account holds all records, including billing,
            participants, and registrations.
          </p>

          <ItemCard className="max-w-none">
            {renderUser(primaryAccount)}
          </ItemCard>

          {linkedAccounts.length > 0 && store.isAdminArea && (
            <div className="pt-4">
              <BorderButton onClick={changePrimary}>
                <i className="far fa-shuffle text-organization" /> Change
                Primary
              </BorderButton>
            </div>
          )}
        </ItemCard>

        <div className="pb-14">
          <ItemCard loading>
            <p className="mb-2 font-bold">Linked Accounts</p>
            <p>
              Allow others to sign in and manage this account using their own
              email and phone number.
            </p>

            {linkedAccounts.map((u) => {
              return (
                <ItemCard key={u.id} className="mb-4 max-w-none">
                  {renderUser(u)}
                </ItemCard>
              );
            })}

            {store.isAdminArea && (
              <div className="flex items-center gap-4">
                <BorderButton
                  to={`/admin/users/${primaryAccount.id}/access/add`}
                >
                  <i className="far fa-plus mr-1 text-organization" /> Add
                  Linked Account
                </BorderButton>
              </div>
            )}
          </ItemCard>

          <ItemCard loading>
            <p className="mb-2 font-bold">Additional Contacts</p>
            <p>
              Emails and text messages sent to the primary account will also be
              sent to these additional contacts. Additional contacts do not have
              account access and are not available to directly contact anywhere
              in Captyn.
            </p>

            <AccountOwnerNotifications
              user={props.user}
              onUserProfileUpdate={props.onUserProfileUpdate}
            />
          </ItemCard>
        </div>
      </Container>
    </>
  );
}
